import {
  FluidPopoverConfig,
  FluidPopoverTranslationOptions,
  FluidSize,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

// ================================================================ //
// -- Internal Types

export type FluidIconRef = {
  icon: string;
  scale: number;
  standalone?: boolean;
};

export type FluidIconStyleProps = {
  iconPosition: Record<string, string>;
  iconStyle: string | { [className: string]: boolean };
  containerStyle: Record<string, string>;
  containerClass: Record<string, boolean>;
  ariaProps: Record<string, string | boolean>;
};

// ================================================================ //
// -- BaseIcon

interface BaseIconProps extends FluidIconStyleProps {
  iconRef: FluidIconRef;
  includeAdornment: boolean;
  iconSize: FluidSize;
}
export function BaseIcon(props: BaseIconProps) {
  const {
    iconPosition,
    iconRef,
    iconStyle,
    includeAdornment,
    ariaProps,
    containerClass,
    containerStyle,
    iconSize,
  } = props;
  return (
    <div style={containerStyle} class={containerClass}>
      <i
        {...ariaProps}
        role='img'
        style={iconPosition}
        innerHTML={iconRef?.icon}
        class={iconStyle}
      />
      {includeAdornment && <span class={`icon-badge ${iconSize}`} />}
    </div>
  );
}

// ================================================================ //
// -- IconSlot

interface IconSlotProps {
  slotName: string;
}

export function IconSlot(props: IconSlotProps) {
  const { slotName } = props;
  return <slot name={slotName} />;
}

// ================================================================ //
// -- IconWithPopover

export function getPopoverProps() {
  return {
    width: this.popoverWidth,
    persistable: this.popoverPersistable,
    placement: this.popoverPlacement,
    theme: this.theme,
    overrideGlobalTheme: true,
    ignoreClippingBoundaries: this.popoverIgnoreClippingBoundaries,
    ...this._popoverConfig, // Integrate 'config' AFTER individual Props so that it isn't overwritten by undefined
    parentContainer: this.popoverParentContainer,
    disableInteraction: this.disabled,
  } as FluidPopoverConfig;
}
interface IconWithPopoverProps {
  popoverConfig: FluidPopoverConfig;
  fluidTranslate: { popoverConfig: FluidPopoverTranslationOptions };
  renderIcon: () => any;
}
export function IconWithPopover(props: IconWithPopoverProps) {
  const { popoverConfig, fluidTranslate, renderIcon } = props;
  return (
    <fluid-popover
      class='icon-popover'
      {...popoverConfig}
      fluidTranslate={fluidTranslate?.popoverConfig}>
      <div slot='trigger'>{renderIcon()}</div>
    </fluid-popover>
  );
}
